<script setup lang="ts">
  import { ref } from "vue";
  import { Layers2, Plus } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Button } from "@/components/ui/button";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { useGridRowsStore, type GridColumnOption } from "@stores/survey/grids/gridRows";

  defineProps<{ columns: GridColumnOption[] }>();

  const emit = defineEmits<{
    "add-column": [column: GridColumnOption];
    "add-recode": [];
    "ask-question": [];
  }>();

  const { columnLabel } = useGridRowsStore();

  const menuOpen = ref(false);

  const addColumn = (column: GridColumnOption) => {
    menuOpen.value = false;
    emit("add-column", column);
  };

  const pick = (event: "add-recode" | "ask-question") => {
    menuOpen.value = false;
    if (event === "add-recode") emit("add-recode");
    else emit("ask-question");
  };
</script>

<template>
  <Popover v-model:open="menuOpen">
    <PopoverTrigger as-child>
      <Button variant="outline">
        <Plus class="size-4" />
        Add column
      </Button>
    </PopoverTrigger>
    <PopoverContent
      align="end"
      class="w-72 p-1">
      <div class="heading-label px-2 py-1.5">Survey questions and recodes</div>
      <div class="max-h-56 overflow-y-auto">
        <button
          v-for="column in columns"
          :key="column.id"
          type="button"
          class="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-13 hover:bg-muted"
          @click="addColumn(column)">
          <span class="min-w-0 flex-1 truncate">{{ columnLabel(column) }}</span>
          <Layers2
            v-if="column.is_for_analytics"
            class="size-3.5 shrink-0 text-muted-foreground" />
        </button>
        <p
          v-if="!columns.length"
          class="px-2 py-1.5 text-13 text-muted-foreground">
          All survey questions are in the grid.
        </p>
      </div>
      <div class="my-1 border-t" />
      <button
        type="button"
        class="flex w-full cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-left text-13 hover:bg-muted"
        @click="pick('add-recode')">
        <Layers2 class="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <span class="flex min-w-0 flex-col">
          New recode
          <span class="text-xs leading-snug text-muted-foreground">A variable computed on every respondent, also a dashboard card</span>
        </span>
      </button>
      <button
        type="button"
        class="flex w-full cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-left text-13 hover:bg-muted"
        @click="pick('ask-question')">
        <AiStar class="mt-0.5 size-4 shrink-0" />
        <span class="flex min-w-0 flex-col">
          Ask AI question
          <span class="text-xs leading-snug text-muted-foreground">A free-text answer per respondent, in this grid only</span>
        </span>
      </button>
    </PopoverContent>
  </Popover>
</template>
